import type { CaptureStatus, WorkLogEventRow } from '../../data/database.types';
import { formatInstant } from '../../lib/timezone';

/**
 * THE WORK LOG, IN WORDS — M6 / S06-01.
 *
 * Crewlink has been recording provenance since M3: every Capture creation and
 * every photo add, removal and reclassification is written in the same
 * transaction as the change itself. **None of it has ever been visible.** This
 * turns those rows into sentences.
 *
 * WHAT A LINE SAYS, AND NOTHING MORE: **what happened · who did it · when.**
 * Photo ids, positions, sequence numbers, event-type identifiers and the raw
 * `detail` object are all internal, and none of them reaches the screen. A man
 * on a roof does not need a UUID.
 *
 * **`capture_created.detail.transcript` IS NEVER READ HERE — not once, not as
 * a preview, not as a fallback.** That field is a QUOTE kept for provenance
 * (D-016), and a copy is allowed to fall behind the thing it copied. The
 * Capture's transcript comes from `capture_transcript_version` and only from
 * there; a memory product that rendered the stale copy would be showing a lie
 * about what was said. `describeWorkLogEvent` therefore says "Capture created"
 * and stops.
 *
 * AN UNKNOWN EVENT IS SHOWN, NOT HIDDEN. Later M6 slices add event types, and
 * a renderer that silently dropped what it did not recognise would make the
 * history quietly incomplete — the one thing a history must never be. Anything
 * unrecognised reads "Capture updated": true of every event this table can
 * hold, and honest about knowing no more than that.
 */

/** Deliberately plain. The Work Log is a record, not a feed. */
export const WORK_LOG_UNKNOWN = 'Capture updated';

/** Neither an AI nor an unknown actor is ever attributed to a person. */
export const WORK_LOG_SYSTEM_ACTOR = 'Crewlink';

const classWord = (value: unknown): 'Before' | 'After' | null =>
  value === 'before' ? 'Before' : value === 'after' ? 'After' : null;

/**
 * A status, in the word Brandon already sees on the chip — without the glyph.
 *
 * The glyph belongs to the status CHIP, where colour must never be the only
 * signal. Inside a sentence it would read as punctuation, so the label is
 * used for its word and the sentence carries the meaning.
 */
const STATUS_WORD: Record<CaptureStatus, string> = {
  open: 'Open',
  repaired: 'Repaired',
  closed: 'Closed',
};

const statusWord = (value: unknown): string | null =>
  typeof value === 'string' && value in STATUS_WORD ? STATUS_WORD[value as CaptureStatus] : null;

/**
 * The field names carried by an `organization_corrected` event (migration 0012).
 *
 * **ONLY THE NAMES, NEVER THE VALUES.** The database records which fields a
 * person actually changed; the values live in `capture_organization`, which is
 * where the screen reads them. A history that quoted the old and new location
 * would become a second store of it, and the second store is always the one
 * that goes stale — the same reason `transcript_edited` says four words.
 *
 * Unknown names are dropped rather than printed: `detail` is written by the
 * writer function, but a renderer that echoed whatever string it found would
 * put unreviewed text on Brandon's screen.
 */
const CORRECTED_FIELD_WORD: Record<string, string> = {
  location: 'Location',
  caption: 'Caption',
};

const correctedFields = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const words: string[] = [];
  for (const name of value) {
    const word = typeof name === 'string' ? CORRECTED_FIELD_WORD[name] : undefined;
    if (word && !words.includes(word)) words.push(word);
  }
  return words;
};

const detailOf = (event: WorkLogEventRow): Record<string, unknown> => event.detail ?? {};

/**
 * One sentence for one event.
 *
 * Every branch degrades to something TRUE rather than to something detailed: a
 * photo event whose class is missing says "Photo added" instead of guessing
 * Before or After, because guessing the class is exactly the inference PT §9
 * forbids anywhere else in the product.
 */
export function describeWorkLogEvent(event: WorkLogEventRow): string {
  const detail = detailOf(event);

  switch (event.event_type) {
    case 'capture_created':
      // NOT the transcript. See the header — this is the whole point.
      return 'Capture created';

    case 'photo_added': {
      const side = classWord(detail.photo_class);
      return side ? `${side} photo added` : 'Photo added';
    }

    case 'photo_removed': {
      const side = classWord(detail.photo_class);
      // "Removed", never "deleted": removal is non-destructive (PT §9) and the
      // history is precisely where that distinction has to hold.
      return side ? `${side} photo removed` : 'Photo removed';
    }

    case 'transcript_edited':
      // M6 / S06-03. FOUR WORDS, and deliberately not the words that changed.
      // `detail` carries only version numbers; the text itself lives in
      // capture_transcript_version, where "View previous version" reads it.
      // A history that quoted transcripts would become a second transcript
      // store, and the second store is always the one that goes stale.
      return 'Transcript edited';

    case 'status_changed': {
      // M6 / S06-02. Both ends or neither: a half-known status change would
      // have to invent the missing side, and an invented status in a history
      // is worse than an unspecific one.
      const from = statusWord(detail.from_status);
      const to = statusWord(detail.to_status);
      if (from && to && from !== to) return `Status changed from ${from} to ${to}`;
      return 'Status changed';
    }

    case 'organization_corrected': {
      // M7 / S07-03. A person overrode what Crewlink worked out, and the
      // history says WHICH fields — never their contents, and never a
      // field the writer did not actually change. The writer only records a
      // field that genuinely differed, so this line cannot claim a
      // correction that did not happen.
      const fields = correctedFields(detail.fields);
      if (fields.length === 2) return 'Location and caption corrected';
      if (fields.length === 1) return `${fields[0]} corrected`;
      return 'Organization corrected';
    }

    case 'photo_reclassified': {
      const from = classWord(detail.from_class);
      const to = classWord(detail.to_class);
      if (from && to && from !== to) return `Photo moved from ${from} to ${to}`;
      return 'Photo moved';
    }

    default:
      return WORK_LOG_UNKNOWN;
  }
}

/**
 * Who did it, with no directory lookup and no invented identity.
 *
 * **`You` is the fallback, and it is honest in v1 because RLS scopes these rows
 * to the reader's own Captures and every writer stamps `auth.uid()` — so a
 * `user` event on a Capture Brandon can see was performed by Brandon.** The
 * display name is used when his profile genuinely carries one; no profile,
 * directory or membership infrastructure is built to find a nicer word, and
 * none should be until there is a second user to need it.
 *
 * `ai` and `sol_confirmed` exist in the schema's vocabulary and nothing writes
 * them yet. They must not crash a renderer that meets one, and they must not
 * be dressed as a person.
 */
export function describeWorkLogActor(
  event: WorkLogEventRow,
  viewer: { userId: string | null; displayName: string | null },
): string {
  if (event.actor_kind !== 'user') return WORK_LOG_SYSTEM_ACTOR;

  const name = viewer.displayName?.trim();
  const isViewer = viewer.userId !== null && event.actor_user_id === viewer.userId;
  if (isViewer && name && name.length > 0) return name;
  return 'You';
}

export interface WorkLogLine {
  readonly id: string;
  readonly summary: string;
  readonly actor: string;
  /**
   * Formatted in the CANONICAL Crewlink timezone, or null.
   *
   * **Null when the profile has no timezone, and the device's is never used
   * instead** (INV-T17). A wrong timestamp on a site record is worse than a
   * missing one — the same rule the Capture header already follows.
   */
  readonly time: string | null;
}

export function buildWorkLogLines(input: {
  readonly events: readonly WorkLogEventRow[];
  readonly viewerUserId: string | null;
  readonly viewerDisplayName: string | null;
  readonly timeZone: string | null;
}): WorkLogLine[] {
  const { events, viewerUserId, viewerDisplayName, timeZone } = input;
  return events.map((event) => ({
    id: event.id,
    summary: describeWorkLogEvent(event),
    actor: describeWorkLogActor(event, { userId: viewerUserId, displayName: viewerDisplayName }),
    time: timeZone ? formatInstant(event.occurred_at, timeZone) : null,
  }));
}
